import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import type { UserPermissionsData, ModulePermissions } from "@/shared/components/users/UserPermissions";
import { createDefaultPermissions } from "@/shared/components/users/UserPermissions";
import { normalizeInstitutionalEmail } from "@/shared/utils/email";

interface UsePermissionsReturn {
  permissions: UserPermissionsData;
  loading: boolean;
  isMaster: boolean;
  qualificacao: string | null;
  hasPermission: (module: keyof UserPermissionsData, op: keyof ModulePermissions) => boolean;
}

const MASTER_EMAIL = "alencar@ferruginiadvogados.com.br";

/**
 * Cache global de profiles — evita que N instâncias de usePermissions
 * (AppSidebar, PermissionGuard, HubInstituicoes, etc.) disparem N SELECTs
 * duplicados na mesma tabela `profiles`.
 */
let _cachedProfile: { userId: string; data: any; ts: number } | null = null;
const CACHE_TTL = 120_000;

async function fetchProfileCached(userId: string) {
  if (_cachedProfile && _cachedProfile.userId === userId && _cachedProfile.data && Date.now() - _cachedProfile.ts < CACHE_TTL) {
    return _cachedProfile.data;
  }

  const isColumnMissingError = (msg: string | undefined) =>
    /column.*does not exist|does not exist|could not find the .* column.*schema cache/i.test(msg || "");

  const { data, error } = await supabase
    .from("profiles")
    .select("qualificacao, permissoes, email, nome")
    .eq("user_id", userId)
    .eq("status", "ativo")
    .maybeSingle();

  let profile = data;
  if (error || !profile) {
    const { data: anyStatusProfile, error: errAny } = await supabase
      .from("profiles")
      .select("qualificacao, permissoes, email, nome, status")
      .eq("user_id", userId)
      .maybeSingle();
    profile = anyStatusProfile;
    if (errAny && isColumnMissingError(errAny.message)) {
      const { data: legacyProfile } = await supabase
        .from("profiles")
        .select("qualificacao, permissoes, email, nome")
        .eq("user_id", userId)
        .maybeSingle();
      profile = legacyProfile ? { ...legacyProfile, status: "ativo" } : null;
    }
  }

  if (profile) {
    _cachedProfile = { userId, data: profile, ts: Date.now() };
    (globalThis as any).__ferruginiProfileCache = {
      userId,
      nome: (profile as any).nome,
      qualificacao: (profile as any).qualificacao,
      ts: Date.now(),
    };
  }
  return profile;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissionsData>(createDefaultPermissions());
  const [loading, setLoading] = useState(true);
  const [isMaster, setIsMaster] = useState(false);
  const [qualificacao, setQualificacao] = useState<string | null>(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const runId = ++runIdRef.current;

    const fetchProfile = async () => {
      const profile = await fetchProfileCached(user.id);
      if (runId !== runIdRef.current) return;

      if (!profile) {
        setPermissions(createDefaultPermissions());
        setIsMaster(false);
        setQualificacao(null);
        setLoading(false);
        return;
      }

      const profileEmail = normalizeInstitutionalEmail((profile as { email?: string | null }).email ?? "");
      const authEmail = normalizeInstitutionalEmail(user.email ?? "");
      const master =
        profile.qualificacao === "socio_gestor" ||
        profileEmail === MASTER_EMAIL ||
        authEmail === MASTER_EMAIL;
      setIsMaster(master);
      setQualificacao(profile.qualificacao);

      if (master) {
        // Sócio Gestor: todas as permissões liberadas automaticamente
        const all = createDefaultPermissions();
        for (const key of Object.keys(all) as (keyof UserPermissionsData)[]) {
          all[key] = { visualizar: true, incluir: true, alterar: true, excluir: true };
        }
        setPermissions(all);
      } else {
        const defaults = createDefaultPermissions();
        const raw = (profile.permissoes as Record<string, Record<string, boolean>>) || {};
        const merged = { ...defaults };
        for (const [k, v] of Object.entries(raw)) {
          if (k in merged) {
            merged[k as keyof UserPermissionsData] = {
              visualizar: false,
              incluir: false,
              alterar: false,
              excluir: false,
              ...(v || {}),
            };
          }
        }
        // Sócios (não gestores) precisam cadastrar e gerir usuários internos mesmo sem JSON antigo completo.
        if (profile.qualificacao === "socio") {
          merged.administracao = { ...merged.administracao, visualizar: true };
          merged.usuarios = {
            visualizar: true,
            incluir: true,
            alterar: true,
            excluir: true,
          };
        }
        // Perfil interno sem nenhuma chave em `permissoes`: o merge fica só com defaults (tudo false)
        // e a sidebar some exceto itens sem `module` (ex.: Arquivo Digital). Libera só visualização
        // dos hubs principais até o cadastro de permissões JSON ser preenchido.
        const rawKeys = Object.keys(raw).filter((k) => k in merged);
        const internalQualForHubDefaults = ["socio", "associado", "estagiario", "colaborador", "controller"];
        if (
          rawKeys.length === 0 &&
          profile.qualificacao &&
          internalQualForHubDefaults.includes(profile.qualificacao)
        ) {
          const hubKeys: (keyof UserPermissionsData)[] = [
            "dashboard",
            "gestao_juridica",
            "gestao_administrativa",
            "financeiro",
            "auditoria",
            "instituicoes",
          ];
          for (const key of hubKeys) {
            merged[key] = { ...merged[key], visualizar: true };
          }
        }
        setPermissions(merged as UserPermissionsData);
      }

      setLoading(false);
    };

    fetchProfile();
  }, [user]);

  const hasPermission = useCallback(
    (module: keyof UserPermissionsData, op: keyof ModulePermissions): boolean => {
      if (isMaster) return true;
      const direct = permissions[module]?.[op] ?? false;
      if (direct) return true;

      // Compatibilidade de entrada no sistema:
      // se o usuário tem qualquer permissão de visualização em algum módulo,
      // garante acesso ao dashboard inicial mesmo sem a flag explícita.
      if (module === "dashboard" && op === "visualizar") {
        const hasAnyModuleAccess = Object.entries(permissions).some(([key, modPerms]) => {
          if (key === "dashboard") return false;
          return Boolean(modPerms?.visualizar || modPerms?.incluir || modPerms?.alterar || modPerms?.excluir);
        });
        if (hasAnyModuleAccess) return true;
      }

      // Compatibilidade geral entre módulos legados e módulos detalhados.
      // Evita bloquear fluxos quando o perfil foi cadastrado em uma versão
      // anterior do modelo de permissões.
      const aliases: Record<string, Array<keyof UserPermissionsData>> = {
        // Hubs de gestão: se tem qualquer módulo-filho, libera o hub-pai
        gestao_juridica: ["processos", "calculos", "criacao_ia", "formatacao", "peticoes", "documentos", "meus_documentos"],
        gestao_administrativa: ["normas_procedimentos", "normas_ia", "administracao", "sistema", "permissoes", "usuarios", "relatorios", "manual", "criacao_ia", "tipos_documentos"],

        // Núcleo IA/Formatação (rotas podem transitar entre esses módulos)
        criacao_ia: ["peticoes", "documentos", "formatacao"],
        peticoes: ["criacao_ia", "documentos", "formatacao"],
        documentos: ["peticoes", "criacao_ia", "formatacao"],
        formatacao: ["documentos", "peticoes", "criacao_ia"],

        // Financeiro detalhado <-> financeiro geral
        fin_clientes: ["financeiro"],
        fin_lancamentos: ["financeiro"],
        fin_contratos: ["financeiro"],
        fin_gestao_contratos: ["financeiro"],
        fin_fornecedores: ["financeiro"],
        fin_fechamento: ["financeiro"],
        fin_producao: ["financeiro"],
        fin_profissionais: ["financeiro"],
        fin_rentabilidade: ["financeiro"],
        fin_inadimplencia: ["financeiro"],
        fin_quitacao: ["financeiro"],
        fin_vencimentos: ["financeiro"],
        fin_rateio_templates: ["financeiro"],
        fin_ia: ["financeiro"],
        fin_relatorios: ["financeiro"],
        fin_captadores_externos: ["financeiro"],

        // Cálculos detalhados <-> cálculos geral
        calc_tributario: ["calculos"],
        calc_trabalhista: ["calculos"],
        calc_monetario: ["calculos"],
        calc_prazos: ["calculos"],

        // Auditorias detalhadas <-> auditoria geral
        audit_tributaria: ["auditoria"],
        audit_trabalhista: ["auditoria"],
      };

      const related = aliases[module as string] ?? [];
      if (related.some((m) => Boolean(permissions[m]?.[op]))) {
        return true;
      }

      return false;
    },
    [permissions, isMaster]
  );

  return { permissions, loading, isMaster, qualificacao, hasPermission };
};
